import { Build, Component, Element, Event, EventEmitter, Host, Prop, State, VNode, h } from '@stencil/core';
import {
  FormComponent,
  HiddenFormElementSlot,
  Validator,
  assignFilteredObject,
  connectForm,
  defaultValidator,
  disconnectForm,
  guid,
  initializeValidators,
} from '../../utils';
import { FontSize, StrokeWidth } from '../types';
import { Option } from './types';

@Component({
  shadow: true,
  styleUrl: 'select.css',
  tag: 'abds-select',
})
export class Select implements FormComponent {
  //--------------------------------------------------------------------------
  //
  // Element
  //
  //--------------------------------------------------------------------------

  @Element() host: HTMLAbdsSelectElement;

  //--------------------------------------------------------------------------
  //
  // Props
  //
  //--------------------------------------------------------------------------

  /** Disables select interaction. */
  @Prop({ reflect: true }) disabled = false;

  /** Allows setting error state. */
  @Prop({ reflect: true }) error = false;

  /** Custom error message. */
  @Prop({ reflect: true }) errorMessage: string;

  /** Text of the information message. */
  @Prop({ reflect: true }) helperText: string;

  /** Sets label text in embedded abds-label. */
  @Prop({ reflect: true }) label: string;

  /** Sets name attribute. */
  @Prop({ reflect: true }) name!: string;

  /** Optional placeholder text. */
  @Prop({ reflect: true }) placeholder: string;

  /** Sets required state. */
  @Prop({ reflect: true }) required = false;

  /** Specifies a custom error message for required state. */
  @Prop({ reflect: true }) requiredMessage: string;

  /** Sets tooltip text in embedded abds-label. */
  @Prop({ reflect: true }) tooltip: string;

  /**
   * The content of this attribute represents the value to be submitted with the form. If this attribute is omitted,
   * the value is taken from the text content of the option element.
   */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) value: string;

  /** Sets select width to default size or full. */
  @Prop({ reflect: true }) width: 'default' | 'full' = 'default';

  /** Hidden element needed for form submission. */
  hiddenElement: HTMLSelectElement;

  //--------------------------------------------------------------------------
  //
  // Private Properties
  //
  //--------------------------------------------------------------------------

  /** Watches for added or removed abds-select-option child nodes */
  private hostMutationObserver: MutationObserver;

  /** Default selected index to revert to on reset */
  private initialSelectedIndex: number;

  /** Text for associated native label element */
  private linkedLabelText: string;

  /** Visible styled select element within shadow DOM. */
  private nativeSelect: HTMLSelectElement;

  /** Unique id to input. */
  private selectId: string;

  /** Instance validation. */
  private validator: Validator<any> = defaultValidator();

  //--------------------------------------------------------------------------
  //
  // Private Methods
  //
  //--------------------------------------------------------------------------

  private onBlur = (): void => {
    this.setIsInvalid();
  };

  private onChange = (): void => {
    this.toggleSelectClassList(this.nativeSelect);
    this.setIsInvalid();
    this.setValue(this.nativeSelect.value);

    // Emission needs to happen last to include all updates to element (i.e. value).
    this.abdsChange.emit();
  };

  private onFocus = (): void => {
    this.nativeSelect.focus();
  };

  private setHiddenElementOptions = (options: Option[]): void => {
    if (options.length) {
      this.hiddenElement.innerHTML = '';

      options.forEach(({ disabled, hidden, innerHTML, selected, value }) => {
        const option = document.createElement('option');

        assignFilteredObject(option, {
          disabled,
          hidden,
          innerHTML,
          selected,
          value,
        });

        this.hiddenElement.appendChild(option);
      });
    }
  };

  private setInitialSelectedIndex = (index: number): void => {
    this.initialSelectedIndex = index;
  };

  private setOptions = (options: HTMLAbdsSelectOptionElement[]): void => {
    this.options = options.map(
      ({ disabled, hidden, innerHTML, label, selected, value }: HTMLAbdsSelectOptionElement) => ({
        disabled,
        hidden,
        innerHTML,
        label,
        selected,
        value,
      })
    );

    if (this.placeholder)
      this.options.unshift({
        disabled: true,
        hidden: true,
        innerHTML: this.placeholder,
        label: null,
        selected: true,
        value: '',
      });
  };

  private setIsInvalid = (isInvalid = !this.validator.validate(this.nativeSelect)): void => {
    this.isInvalid = isInvalid;
  };

  private setValue = (value: string): void => {
    this.value = value;
  };

  private toggleSelectClassList = (select: HTMLSelectElement) => {
    const { selectedIndex, value: selectedValue } = select;

    select.classList.toggle('disabled-selected', select.options[selectedIndex]?.disabled);
    select.classList.toggle('placeholder', !!(selectedIndex === 0 && selectedValue === ''));
  };

  //--------------------------------------------------------------------------
  //
  // Private State
  //
  //--------------------------------------------------------------------------

  @State() isInvalid = false;

  @State() options: Option[] = [];

  // --------------------------------------------------------------------------
  //
  //  Public Methods
  //
  // --------------------------------------------------------------------------

  /** Method for select to provide form reset behavior. */
  onFormReset = (): void => {
    this.nativeSelect.selectedIndex = this.initialSelectedIndex;
  };

  //--------------------------------------------------------------------------
  //
  //  Events
  //
  //--------------------------------------------------------------------------

  /** Fired only when value is committed (e.g. pressing the enter key). */
  @Event() abdsChange: EventEmitter;

  //--------------------------------------------------------------------------
  //
  // Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  componentDidLoad(): void {
    this.setHiddenElementOptions(this.options);
    this.setInitialSelectedIndex(this.nativeSelect.selectedIndex);

    if (this.nativeSelect?.options?.length) this.toggleSelectClassList(this.nativeSelect);
  }

  connectedCallback(): void {
    const options = [...this.host.querySelectorAll('abds-select-option')];

    if (options.length) {
      this.setOptions(options);
      if (this.nativeSelect) this.setValue(this.nativeSelect.value);
    }

    this.selectId = this.host.id || `select-${guid()}`;

    this.linkedLabelText = document.querySelector(`label[for="${this.host.id}"]`)?.textContent;

    this.validator = initializeValidators(this);

    if (Build.isBrowser) {
      this.hostMutationObserver = new MutationObserver(() => {
        const options = [...this.host.querySelectorAll('abds-select-option')];

        if (options.length) {
          this.setHiddenElementOptions(options);
          this.setOptions(options);

          /**
           * Need to wait after setting options for component to rerender, updating nativeSelect values.
           * TODO: Investigate alternatives to setTimeout (e.g., lifecycle methods, etc.).
           */
          setTimeout(() => {
            const { selectedIndex, value: selectedValue } = this.nativeSelect;

            this.setValue(selectedValue);
            this.setInitialSelectedIndex(selectedIndex);
            this.toggleSelectClassList(this.nativeSelect);
          }, 100);
        }
      });

      this.hostMutationObserver.observe(this.host, { attributes: true, childList: true });
    }

    connectForm(this);
  }

  disconnectedCallback(): void {
    this.hostMutationObserver.disconnect();
    disconnectForm(this);
  }

  render(): VNode {
    const config = {
      icon: {
        icon: 'arrow-down-1',
        size: 'md' as FontSize,
        'stroke-width': 'semibold' as StrokeWidth,
      },
      label: {
        required: this.required,
        tooltip: this.tooltip,
      },
      select: {
        'aria-describedby': this.errorMessage || this.helperText || this.isInvalid ? this.selectId : undefined,
        'aria-label': this.label || this.linkedLabelText || undefined,
        disabled: this.disabled,
        name: this.name,
        onBlur: this.onBlur,
        onChange: this.onChange,
        ref: (select: HTMLSelectElement) => {
          this.nativeSelect = select;
        },
        required: this.required,
      },
      supportingText: {
        'error-text': (this.isInvalid && this.validator.errorMessage) || (this.error && this.errorMessage) || undefined,
        'helper-text': this.helperText,
        id: this.selectId,
      },
    };
    const hasSupportingText = (this.error && this.errorMessage) || this.helperText || this.isInvalid;

    return (
      <Host onClick={this.onFocus}>
        {this.label && <abds-label {...config.label}>{this.label}</abds-label>}

        <div class={{ 'select-container': true, invalid: this.isInvalid || this.error }}>
          <select {...config.select}>
            {this.options.map(({ innerHTML, ...optionProps }) => (
              <option {...optionProps}>{innerHTML}</option>
            ))}
          </select>
          <abds-icon {...config.icon}></abds-icon>
        </div>

        {hasSupportingText && <abds-supporting-text {...config.supportingText}></abds-supporting-text>}

        <HiddenFormElementSlot component={this} type="select" />
      </Host>
    );
  }
}
